import React, { useEffect, useId, useRef, useState } from "react";

import { filterAutocompleteOptions } from "../../../utils/helpers/autoComplete/autoCompleteHelper";

import "./AutoCompleteTextField.css";

export default function AutoCompleteTextField({
  label,
  name,
  value = "",
  onChange,
  options = [],
  placeholder = "Start typing...",
  error,
  required = false,
  disabled = false,
  maxLength = 100,
  limit = 8,
  className = "",
}) {
  const id = useId();
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputId = `${id}-input`;
  const listId = `${id}-options`;
  const errorId = `${id}-error`;

  const matches = filterAutocompleteOptions(options, value ?? "", limit);

  const showSuggestions =
    isOpen &&
    !disabled &&
    String(value ?? "").trim().length > 0 &&
    matches.length > 0;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);

    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }, [disabled]);

  const closeSuggestions = () => {
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleChange = (event) => {
    onChange?.(event);
    setIsOpen(true);
    setActiveIndex(-1);
  };

  const handleSelect = (option) => {
    onChange?.({
      target: {
        name,
        value: option.value,
      },
    });

    closeSuggestions();
    inputRef.current?.focus();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      if (isOpen) event.stopPropagation();
      closeSuggestions();
      return;
    }

    if (event.key === "Tab") {
      closeSuggestions();
      return;
    }

    if (!matches.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((previous) =>
        previous < matches.length - 1 ? previous + 1 : 0,
      );
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((previous) =>
        previous > 0 ? previous - 1 : matches.length - 1,
      );
    }

    if (event.key === "Enter" && showSuggestions && activeIndex >= 0) {
      event.preventDefault();
      handleSelect(matches[activeIndex]);
    }
  };

  return (
    <div
      ref={containerRef}
      className={[
        "ko-autocomplete",
        disabled && "ko-autocomplete--disabled",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label && (
        <label htmlFor={inputId} className="ko-autocomplete__label">
          {label}

          {required && (
            <span className="ko-autocomplete__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}

      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="text"
        value={value ?? ""}
        onChange={handleChange}
        onFocus={() => setIsOpen(true)}
        onBlur={(event) => {
          if (!containerRef.current?.contains(event.relatedTarget)) {
            closeSuggestions();
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={maxLength}
        required={required}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showSuggestions}
        aria-controls={showSuggestions ? listId : undefined}
        aria-activedescendant={
          showSuggestions && activeIndex >= 0
            ? `${id}-option-${activeIndex}`
            : undefined
        }
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        className={[
          "ko-autocomplete__input",
          error && "ko-autocomplete__input--error",
        ]
          .filter(Boolean)
          .join(" ")}
      />

      {showSuggestions && (
        <div id={listId} className="ko-autocomplete__options" role="listbox">
          {matches.map((option, index) => (
            <div
              key={option.value}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={[
                "ko-autocomplete__option",
                index === activeIndex && "ko-autocomplete__option--active",
              ]
                .filter(Boolean)
                .join(" ")}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}

      {error && (
        <span id={errorId} className="ko-autocomplete__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
